import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as reportSchedulesApi from '../api/reportSchedules';
import * as reportProfilesApi from '../api/reportProfiles';
import * as deliveryConfigsApi from '../api/deliveryConfigs';

export function ReportSchedules() {
  const { selectedOrg } = useOrg();
  const { isAdmin, canReadAssets } = usePermissions();
  const [schedules, setSchedules] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [deliveryConfigs, setDeliveryConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(null);
  const [form, setForm] = useState({
    profileId: '',
    name: '',
    frequency: 'monthly',
    dayOfWeek: 1,
    dayOfMonth: 1,
    hour: 6,
    deliveryConfigId: '',
    periodOffset: 0
  });

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [schedulesData, profilesData] = await Promise.all([
        reportSchedulesApi.getReportSchedules(selectedOrg.id),
        reportProfilesApi.getReportProfiles(selectedOrg.id, { status: 'published' })
      ]);
      setSchedules(schedulesData.schedules);
      setFrequencies(schedulesData.frequencies);
      setProfiles(profilesData);
      
      if (isAdmin) {
        const configsData = await deliveryConfigsApi.getDeliveryConfigs(selectedOrg.id);
        setDeliveryConfigs(configsData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenCreate = () => {
    setEditingSchedule(null);
    setForm({
      profileId: profiles[0]?.id || '',
      name: '',
      frequency: 'monthly',
      dayOfWeek: 1,
      dayOfMonth: 1,
      hour: 6,
      deliveryConfigId: '',
      periodOffset: 0
    });
    setShowModal(true);
  };

  const handleOpenEdit = (schedule) => {
    setEditingSchedule(schedule);
    setForm({
      profileId: schedule.profileId,
      name: schedule.name,
      frequency: schedule.frequency,
      dayOfWeek: schedule.dayOfWeek || 1,
      dayOfMonth: schedule.dayOfMonth || 1,
      hour: schedule.hour,
      deliveryConfigId: schedule.deliveryConfigId || '',
      periodOffset: schedule.periodOffset || 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    setError(null);
    try {
      const data = { ...form };
      if (!data.deliveryConfigId) delete data.deliveryConfigId;

      if (editingSchedule) {
        await reportSchedulesApi.updateReportSchedule(selectedOrg.id, editingSchedule.id, data);
      } else {
        await reportSchedulesApi.createReportSchedule(selectedOrg.id, data);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!selectedOrg || !window.confirm('Delete this schedule?')) return;
    try {
      await reportSchedulesApi.deleteReportSchedule(selectedOrg.id, scheduleId);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTrigger = async (scheduleId) => {
    if (!selectedOrg) return;
    setTriggering(scheduleId);
    try {
      await reportSchedulesApi.triggerScheduledRun(selectedOrg.id, scheduleId);
      alert('Report run triggered successfully!');
      loadData();
    } catch (err) {
      alert('Failed to trigger run: ' + err.message);
    } finally {
      setTriggering(null);
    }
  };

  const getFrequencyLabel = (freq) => {
    return freq.charAt(0).toUpperCase() + freq.slice(1);
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view report schedules.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Scheduled Reports"
        subtitle="Configure automated report generation and delivery"
      />

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : (
        <>
          {isAdmin && (
            <div style={styles.toolbar}>
              <button onClick={handleOpenCreate} style={styles.createButton} data-testid="add-schedule-button">
                + Add Schedule
              </button>
            </div>
          )}

          <div style={styles.tableContainer}>
            <table style={styles.table} data-testid="schedules-table">
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Profile</th>
                  <th style={styles.th}>Frequency</th>
                  <th style={styles.th}>Time</th>
                  <th style={styles.th}>Next Run</th>
                  <th style={styles.th}>Last Run</th>
                  <th style={styles.th}>Delivery</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map(schedule => (
                  <tr key={schedule.id} style={styles.tr} data-testid={`schedule-${schedule.id}`}>
                    <td style={styles.td}><strong>{schedule.name}</strong></td>
                    <td style={styles.td}>{schedule.profileId}</td>
                    <td style={styles.td}>
                      <span style={styles.frequencyBadge}>{getFrequencyLabel(schedule.frequency)}</span>
                    </td>
                    <td style={styles.td}>{schedule.hour}:00</td>
                    <td style={styles.td}>{new Date(schedule.nextRunAt).toLocaleDateString()}</td>
                    <td style={styles.td}>{schedule.lastRunAt ? new Date(schedule.lastRunAt).toLocaleDateString() : '-'}</td>
                    <td style={styles.td}>{schedule.deliveryConfigId ? '✓ SFTP' : '-'}</td>
                    <td style={styles.td}>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => handleTrigger(schedule.id)}
                            disabled={triggering === schedule.id}
                            style={styles.triggerButton}
                            data-testid={`trigger-${schedule.id}`}
                          >
                            {triggering === schedule.id ? 'Running...' : 'Run Now'}
                          </button>
                          <button
                            onClick={() => handleOpenEdit(schedule)}
                            style={styles.editButton}
                            data-testid={`edit-${schedule.id}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(schedule.id)}
                            style={styles.deleteButton}
                            data-testid={`delete-${schedule.id}`}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {schedules.length === 0 && (
                  <tr>
                    <td colSpan="8" style={styles.emptyState}>No scheduled reports. Click "Add Schedule" to create one.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="schedule-modal">
            <h3 style={styles.modalTitle}>
              {editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
            </h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                style={styles.input}
                placeholder="e.g., Monthly Usage Report"
                data-testid="name-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Report Profile *</label>
              <select
                value={form.profileId}
                onChange={(e) => setForm(prev => ({ ...prev, profileId: e.target.value }))}
                style={styles.select}
                data-testid="profile-select"
              >
                <option value="">Select a profile...</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Frequency *</label>
                <select
                  value={form.frequency}
                  onChange={(e) => setForm(prev => ({ ...prev, frequency: e.target.value }))}
                  style={styles.select}
                  data-testid="frequency-select"
                >
                  {frequencies.map(f => (
                    <option key={f} value={f}>{getFrequencyLabel(f)}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Hour (0-23)</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={form.hour}
                  onChange={(e) => setForm(prev => ({ ...prev, hour: parseInt(e.target.value) }))}
                  style={styles.input}
                  data-testid="hour-input"
                />
              </div>
            </div>
            {form.frequency === 'weekly' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Day of Week</label>
                <select
                  value={form.dayOfWeek}
                  onChange={(e) => setForm(prev => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))}
                  style={styles.select}
                  data-testid="day-of-week-select"
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
            )}
            {(form.frequency === 'monthly' || form.frequency === 'quarterly') && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Day of Month</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={form.dayOfMonth}
                  onChange={(e) => setForm(prev => ({ ...prev, dayOfMonth: parseInt(e.target.value) }))}
                  style={styles.input}
                  data-testid="day-of-month-input"
                />
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>SFTP Delivery (optional)</label>
              <select
                value={form.deliveryConfigId}
                onChange={(e) => setForm(prev => ({ ...prev, deliveryConfigId: e.target.value }))}
                style={styles.select}
                data-testid="delivery-select"
              >
                <option value="">No automatic delivery</option>
                {deliveryConfigs.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Period Offset</label>
              <input
                type="number"
                min="0"
                value={form.periodOffset}
                onChange={(e) => setForm(prev => ({ ...prev, periodOffset: parseInt(e.target.value) }))}
                style={styles.input}
                data-testid="period-offset-input"
              />
              <small style={styles.hint}>Number of periods to look back (0 = most recent)</small>
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name || !form.profileId}
                style={styles.submitButton}
                data-testid="save-schedule-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' },
  createButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  frequencyBadge: { padding: '0.25rem 0.5rem', background: '#ebf8ff', color: '#2b6cb0', borderRadius: '4px', fontSize: '0.75rem' },
  triggerButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #38a169', background: 'white', color: '#38a169', fontSize: '0.75rem', cursor: 'pointer', marginRight: '0.25rem' },
  editButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer', marginRight: '0.25rem' },
  deleteButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #e53e3e', background: 'white', color: '#e53e3e', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '500px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  formGroup: { marginBottom: '1rem', flex: 1 },
  formRow: { display: 'flex', gap: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' },
  input: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  select: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white' },
  hint: { display: 'block', marginTop: '0.25rem', fontSize: '0.75rem', color: '#718096' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  submitButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
